// src/pages/AnalysisResultTabs.js
import React, { useState, useEffect, useRef } from 'react';
import { Tabs, Button, Spin, notification, Empty, Breadcrumb } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { fetchAnalysisResult } from '../slices/analysisResultSlice';
import { useNavigate, useLocation, useParams, Link } from 'react-router-dom';
import './AnalysisResultTabs.css';
import success from '../images/success.png';
import failure from '../images/failure.png';
import ClusterDiagram from '../components/ClusterDiagram';
import InfrastructureTable from '../components/InfrastructureTable';
import { fetchClusterData } from '../slices/clusterSlice';
import { selectTestbed } from '../slices/testbedSlice';
import { API_BASE } from '../config/env';

const arrayBufferToBase64 = (bufferObj) => {
  if (!bufferObj || !bufferObj.data) return '';
  let binary = '';
  const bytes = new Uint8Array(bufferObj.data);
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
};

const AnalysisResultTabs = () => {
  const dispatch   = useDispatch();
  const navigate   = useNavigate();
  const location   = useLocation();
  const { applicationId } = useParams();

  // Name + description from navigation state (robust to different keys)
  const applicationName =
    location.state?.applicationName ?? 'New Application';
  const applicationDescription =
    location.state?.description ??
    location.state?.applicationDescription ??
    'New Description';

  const tbId  = useSelector(selectTestbed); // 'tbA' | 'tbB'
  const isTMA = tbId === 'tbB';             // TMA → hide all analysis tabs

  const { data: analysisResult, loading, error } = useSelector(
    (state) => state.analysisResult || { data: null, loading: false, error: null }
  );


  const {
  data: clusterData,
  loading: clusterStatus,
  error: clusterError,
} = useSelector(
  (state) => state.cluster || { data: null, loading: 'idle', error: null }
);

  // TMA starts at Hardware, TB-A starts at Dataflow
  const [activeTab, setActiveTab] = useState(isTMA ? '3' : '1');

  // polling & ws refs (used only for TB-A)
  const pollingRef = useRef(null);
  const wsRef      = useRef(null);

  // reflect tb change live
  useEffect(() => {
    setActiveTab(isTMA ? '3' : '1');
  }, [isTMA]);

  // Always get cluster data; analysis result only for TB-A
  useEffect(() => {
    dispatch(fetchClusterData());
  }, [dispatch]);

  // If hardware API fails, show a human-readable popup
  useEffect(() => {
    if (!clusterError) return;

    console.error('Hardware / cluster error:', clusterError);

    notification.error({
      message: 'Could not load hardware information',
      description:
        'We were unable to retrieve the hardware topology from the testbed. ' +
        'This may mean the testbed is offline or the connection is misconfigured. ' +
        'Please try again in a moment or contact your system administrator.',
    });
  }, [clusterError]);

  // TB-A only: fetch analysis result & poll
  useEffect(() => {
    if (isTMA || !applicationId) return;

    dispatch(fetchAnalysisResult(applicationId));
    pollingRef.current = setInterval(() => {
      dispatch(fetchAnalysisResult(applicationId));
    }, 10000);

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [dispatch, applicationId, isTMA]);

  // TB-A only: WebSocket to refresh analysis result
  useEffect(() => {
    if (isTMA) return; // do not open WS for TMA

    try {
      const base  = API_BASE.replace(/\/api\/?$/, '');
      const wsUrl = base.replace(/^http/, 'ws');
      const ws    = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onmessage = () => {
        if (applicationId) dispatch(fetchAnalysisResult(applicationId));
      };

      return () => {
        try { ws.close(); } catch (_) {}
        wsRef.current = null;
      };
    } catch (_) {
      return undefined;
    }
  }, [dispatch, applicationId, isTMA]);

  // TB-A only: stop polling once we have something
  useEffect(() => {
    if (isTMA || !analysisResult) return;
    const hasImage = !!analysisResult.file;
    const hasFlags =
      typeof analysisResult.liveness !== 'undefined' ||
      typeof analysisResult.consistency !== 'undefined';
    if (hasImage || hasFlags) {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    }
  }, [analysisResult, isTMA]);

  const handleBack = () => navigate(-1);

  const handleSubmit = () => {
    notification.success({
      message: 'Application Submitted',
      description: 'Application submitted successfully!',
    });
    navigate("/home");
  };

  const handleFinish = () => {
    navigate("/home");
  };

  /* ---------------- TB-A: Dataflow & Software (hidden entirely for TMA) ---------------- */

  const renderDataflowTab = () => {
    let content;
    if (loading) {
      content = (
        <div className="loading-animation">
          <Spin />
          <p>Waiting for analysis results...</p>
        </div>
      );
    } else if (error && !analysisResult) {
      content = (
        <div className="loading-animation">
          <Spin />
          <p>Waiting for Analysis Result</p>
        </div>
      );
    } else if (analysisResult && analysisResult.file) {
      const base64String = arrayBufferToBase64(analysisResult.file);
      const imageSrc = `data:${analysisResult.file.type};base64,${base64String}`;
      content = (
        <img
          src={imageSrc}
          alt="Analysis Result"
          className="dataflow-image"
        />
      );
    } else {
      content = (
        <div className="loading-animation">
          <Spin />
          <p>Still waiting for Analysis Result</p>
        </div>
      );
    }

    return (
      <div className="tab-content">
        {content}
        <div className="button-group">
          <Button size="large" onClick={handleBack}>
            Back
          </Button>
          <Button
            size="large"
            onClick={() => setActiveTab('2')}
            type="primary"
            disabled={!analysisResult}
          >
            Next
          </Button>
        </div>
      </div>
    );
  };

  const renderSoftwareTab = () => {
    const hasResult         = !!analysisResult;
    const livenessStatus    = hasResult ? analysisResult.liveness : null;
    const consistencyStatus = hasResult ? analysisResult.consistency : null;

    return (
      <div className="tab-content">
        <div className="description-section">
          <div className="description-item">
            <span className="description-label">Application Name:</span>
            <span className="description-value">
              {applicationName}
            </span>
          </div>
          <div className="description-item">
            <span className="description-label">Application Description:</span>
            <span className="description-value">
              {applicationDescription}
            </span>
          </div>
        </div>

        <div className="status-section">
          <div className="status-item">
            <p className="status-text">Liveness Verification:</p>
            {hasResult ? (
              livenessStatus ? (
                <img src={success} alt="Success" className="status-image" />
              ) : (
                <img src={failure} alt="Failure" className="status-image" />
              )
            ) : (
              <span className="status-pending-text">Pending...</span>
            )}
          </div>
          <div className="status-item">
            <p className="status-text">Consistency Analysis:</p>
            {hasResult ? (
              consistencyStatus ? (
                <img src={success} alt="Success" className="status-image" />
              ) : (
                <img src={failure} alt="Failure" className="status-image" />
              )
            ) : (
              <span className="status-pending-text">Pending...</span>
            )}
          </div>
        </div>

        <div className="button-group">
          <Button size="large" onClick={() => setActiveTab('1')}>
            Back
          </Button>
          <Button
            size="large"
            onClick={() => setActiveTab('3')}
            type="primary"
          >
            Next
          </Button>
        </div>
      </div>
    );
  };

  /* ---------------- Hardware (shown to both; no analysis mentions for TMA) ---------------- */

  const renderHardwareTab = () => {
  const hasClusterData =
    !!clusterData &&
    (
      (Array.isArray(clusterData) && clusterData.length > 0) ||
      (Array.isArray(clusterData?.nodes) && clusterData.nodes.length > 0) ||
      Object.keys(clusterData).length > 0
    );

  // 🔹 Show loader only while actually pending
  if (clusterStatus === 'pending') {
    return (
      <div className="tab-content">
        <div className="loading-animation">
          <Spin />
          <p>Loading hardware information from the testbed...</p>
        </div>

        <div className="button-group">
          {!isTMA && (
            <Button size="large" onClick={() => setActiveTab('2')}>
              Back
            </Button>
          )}
          <Button size="large" onClick={handleSubmit} type="primary" disabled>
            Submit
          </Button>
        </div>
      </div>
    );
  }

  // 🔹 Empty / error state
  if (!hasClusterData) {
    return (
      <div className="tab-content">
        <div className="hardware-empty-state">
          <Empty
            description={
              <span>
                We couldnt load the hardware information right now.
                <br />
                This may mean the testbed is offline or not yet configured.
              </span>
            }
          />
          <Button
            style={{ marginTop: 16 }}
            onClick={() => dispatch(fetchClusterData())}
          >
            Retry loading hardware
          </Button>
        </div>

        <div className="button-group">
          {!isTMA && (
            <Button size="large" onClick={() => setActiveTab('2')}>
              Back
            </Button>
          )}
          <Button size="large" onClick={handleFinish} type="primary">
            Finish
          </Button>
        </div>
      </div>
    );
  }

  // 🔹 Normal state
  return (
    <div className="tab-content">
      <div className="hardware-flex-container">
        <div className="infrastructure-card">
          <div className="infrastructure-title">Infrastructure</div>
          <InfrastructureTable data={clusterData} />
        </div>
        <div className="hardware-image-container">
          <ClusterDiagram data={clusterData} />
        </div>
      </div>

      <div className="button-group">
        {!isTMA && (
          <Button size="large" onClick={() => setActiveTab('2')}>
            Back
          </Button>
        )}
        <Button size="large" onClick={handleSubmit} type="primary">
          Submit
        </Button>
      </div>
    </div>
  );
};


  // Tabs: TMA sees ONLY Hardware; TB-A sees Dataflow → Software → Hardware
  const tabItems = isTMA
    ? [{ key: '3', label: 'Hardware', children: renderHardwareTab() }]
    : [
        { key: '1', label: 'Dataflow', children: renderDataflowTab() },
        { key: '2', label: 'Software', children: renderSoftwareTab() },
        { key: '3', label: 'Hardware', children: renderHardwareTab() },
      ];

  return (
    <>
    <div className="page-header">
        <div className="page-breadcrumb-wrapper">
          <Breadcrumb>
            <Breadcrumb.Item>
              <Link to="/home">Home</Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <Link to="/service-request">Service Request</Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>Analysis Results</Breadcrumb.Item>
          </Breadcrumb>
        </div>
      </div>
    <div className="analysis-result-tabs-container">
      <Tabs
        activeKey={activeTab}
        items={tabItems}
        onChange={(key) => setActiveTab(key)}
      />
    </div></>
  );
};

export default AnalysisResultTabs;
